import React, { useEffect, useState } from 'react';

import AnimateHeight from 'react-animate-height';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { TextFeedback } from 'src/components/autoreview-feedback/src/text-feedback';
import { ThumbsUpThumbsDown } from 'src/components/autoreview-feedback/src/thumbs-up-thumbs-down';
import { FabricComment } from 'src/components/conversation-provider/types';
import { SendAnalyticsEvent } from 'src/components/conversation/src/components/types';
import {
  actionSubjectIds,
  AutoreviewFeedbackSentiment,
  EventAttributes,
  EventData,
} from 'src/components/conversation/src/internal/analytics';
import {
  DEFAULT_AUTOREVIEW_COMMENT_UUIDS,
  isAutoreviewCommentUUID,
} from 'src/components/conversation/src/utils/is-autoreview-comment-uuid';
import { PullRequest } from 'src/components/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import { useIntl } from 'src/hooks/intl';
import store from 'src/utils/store';

import messages from './autoreview-feedback.i18n';

type StoreValue = {
  collapsed: boolean;
  sentiment: AutoreviewFeedbackSentiment;
};

const AUTOREVIEW_COMMENT_STORE_KEY = 'autoreview-comment-id';

export type Props = {
  comment: FabricComment;
  isFileLevelComment: boolean;
  sendAnalyticsEvent: SendAnalyticsEvent;
  pullRequest?: PullRequest;
  collapseComment?: () => void;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const AutoreviewFeedback: React.FC<Props> = ({
  comment,
  isFileLevelComment,
  sendAnalyticsEvent,
  pullRequest,
  collapseComment,
}) => {
  const [sentiment, setSentiment] =
    useState<AutoreviewFeedbackSentiment>('neutral');
  const [textSubmissionState, setTextSubmissionState] = useState<
    'hidden' | 'pending' | 'submitted'
  >('hidden');
  const { formatMessage } = useIntl();

  const isAutoreviewFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableAutoreviewFeedbackUi
  ) as boolean;

  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.autoreviewCommentUuidConfig,
    DEFAULT_AUTOREVIEW_COMMENT_UUIDS
  );

  const isAutoreviewComment =
    isAutoreviewCommentUUID(comment.createdBy.id, commentUUIDs) &&
    isFileLevelComment;

  const storeId = `${AUTOREVIEW_COMMENT_STORE_KEY}::${comment.commentId}`;

  useEffect(() => {
    const maybeItem: StoreValue | null = store.get(storeId);
    if (maybeItem && collapseComment) {
      setSentiment(maybeItem.sentiment);
      if (maybeItem.collapsed) {
        collapseComment();
      }
    }
  }, [comment.commentId, collapseComment, storeId]);

  if (!isAutoreviewFeedbackEnabled || !isAutoreviewComment) {
    return <></>;
  }

  const commonEventData = (
    additionalAttributes?: EventAttributes
  ): EventData => ({
    objectId: comment.commentId.toString(),
    attributes: {
      prAuthorId: pullRequest?.author?.account_id,
      prURL: pullRequest?.links.html.href,
      commentId: comment.commentId,
      ...additionalAttributes,
    },
  });

  const handleFeedbackClick = (givenSentiment: 'positive' | 'negative') => {
    switch (givenSentiment) {
      case 'positive':
        sendAnalyticsEvent({
          actionSubjectId: actionSubjectIds.autoreviewThumbsUp,
          ...commonEventData({ autoreviewFeedbackSentiment: givenSentiment }),
        });
        store.set(storeId, { collapsed: false, sentiment: 'positive' });
        break;
      case 'negative':
        sendAnalyticsEvent({
          actionSubjectId: actionSubjectIds.autoreviewThumbsDown,
          ...commonEventData({ autoreviewFeedbackSentiment: givenSentiment }),
        });
        store.set(storeId, { collapsed: true, sentiment: 'negative' });
        setTextSubmissionState('pending');
        break;
    }

    setSentiment(givenSentiment);
  };

  const boxStyle = xcss({
    borderRadius: 'border.radius.100',
    backgroundColor: 'color.background.accent.teal.subtlest',
  });

  return (
    <Box padding="space.100" xcss={boxStyle}>
      <Flex gap="space.100">
        <Flex
          alignItems="center"
          justifyContent="center"
          xcss={xcss({ height: '24px' })}
        >
          🤔
        </Flex>
        <Stack xcss={xcss({ flex: 1 })}>
          <ThumbsUpThumbsDown
            sentiment={sentiment}
            handleFeedbackClick={handleFeedbackClick}
          />
          <AnimateHeight
            duration={250}
            animateOpacity
            height={
              textSubmissionState === 'pending' && sentiment === 'negative'
                ? 'auto'
                : 0
            }
          >
            <TextFeedback
              commonEventData={commonEventData}
              setTextSubmissionState={setTextSubmissionState}
              sendAnalyticsEvent={sendAnalyticsEvent}
              collapseComment={collapseComment}
              isOpen={
                textSubmissionState === 'pending' && sentiment === 'negative'
              }
            />
          </AnimateHeight>
          <AnimateHeight
            duration={250}
            animateOpacity
            height={textSubmissionState === 'submitted' ? 'auto' : 0}
          >
            <Flex alignItems="center">
              <CheckCircleIcon
                primaryColor={token('color.icon.success', '#22A06B')}
                secondaryColor={token('color.icon.inverse', '#FFFFFF')}
                label="Submitted"
                size="small"
              />
              <Text>{formatMessage(messages.thankYouMessage)}</Text>
            </Flex>
          </AnimateHeight>
        </Stack>
      </Flex>
    </Box>
  );
};
